import { Badge } from "@/components/ui/badge";
import { formatAmount } from "@/lib/utils";
import Decimal from "decimal.js";
import { useTranslation } from "react-i18next";

function SkontoBadge({
  transaction,
  invoiceTotalAmount,
}: {
  transaction: { amount?: number; side?: "debit" | "credit" } | undefined;
  invoiceTotalAmount: number;
}) {
  const { t } = useTranslation();

  const percentage = matchTypicalSkontoAmount(transaction, invoiceTotalAmount);

  if (!percentage) return null;

  return (
    <Badge
      variant="outline"
      className="bg-gray-50 border-gray-200 text-gray-700 text-[10px] px-1.5 py-0 rounded-full h-[18px]"
    >
      {`${percentage
        .toFixed(2)
        .replace(/\\.00$/, "")
        .replace(".", ",")}% ${t("discount")}`}
    </Badge>
  );
}

function matchTypicalSkontoAmount(
  transaction: { amount?: number; side?: "debit" | "credit" } | undefined,
  invoiceTotalAmount: number,
): number | undefined {
  if (!transaction) return undefined;
  if (!transaction.amount) return undefined;

  const transactionAmount = new Decimal(
    (transaction.side === "debit" ? -1 : 1) * transaction.amount,
  ).abs();
  const invoiceAmount = new Decimal(invoiceTotalAmount).abs();

  const relevantPercentage = [0.01, 0.02, 0.03].map(
    (percentage) => new Decimal(percentage),
  );

  for (const percentage of relevantPercentage) {
    const expectedTransactionAmount = invoiceAmount
      .mul(new Decimal(1).minus(percentage))
      .toDecimalPlaces(2);

    if (transactionAmount.eq(expectedTransactionAmount)) {
      return percentage.mul(100).toNumber();
    }
  }

  return undefined;
}

const ForeignCurrencyBadge = ({
  originalCurrency,
  originalAmount,
}: {
  originalCurrency: string;
  originalAmount: number;
}) => {
  return (
    <p className="text-xs text-muted-foreground">
      {formatAmount(originalAmount, originalCurrency ?? "")}
    </p>
  );
};

export const InvoiceAmountBadge = ({
  transaction,
  invoiceTotalAmount,
  currency,
  originalCurrency,
  originalAmount,
}: {
  transaction: { amount?: number; side?: "debit" | "credit" } | undefined;
  invoiceTotalAmount: number;
  currency: string;
  originalCurrency: string;
  originalAmount: number;
}) => {
  if (currency !== originalCurrency) {
    return (
      <ForeignCurrencyBadge
        originalCurrency={originalCurrency}
        originalAmount={originalAmount}
      />
    );
  }

  return (
    <SkontoBadge
      transaction={transaction}
      invoiceTotalAmount={invoiceTotalAmount}
    />
  );
};
